import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Nodo del árbol de carpetas:
 * {
 *   id: string,
 *   name: string,
 *   parent_id: string | null,
 *   case_id: string,
 *   space_id: string,
 *   children: FolderNode[],
 *   documents: Document[],
 *   depth: number
 * }
 */

// ── Construir árbol desde lista plana ──────────────────────────────────────

const byDocOrder = (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0);

export function buildTree(folders, documents) {
  const folderMap = {};
  const rootNodes = [];

  // Crear nodos
  for (const f of folders) {
    folderMap[f.id] = { ...f, children: [], documents: [] };
  }

  // Asignar documentos a sus carpetas
  for (const doc of documents) {
    if (doc.folder_id && folderMap[doc.folder_id]) {
      folderMap[doc.folder_id].documents.push(doc);
    }
  }

  for (const f of Object.values(folderMap)) f.documents.sort(byDocOrder);

  // Construir jerarquía
  for (const f of folders) {
    if (f.parent_id && folderMap[f.parent_id]) {
      folderMap[f.parent_id].children.push(folderMap[f.id]);
    } else {
      rootNodes.push(folderMap[f.id]);
    }
  }

  // Documentos en raíz (sin folder_id)
  const rootDocuments = documents.filter(d => !d.folder_id).sort(byDocOrder);

  const sortNodes = (nodes) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortNodes(n.children);
  };
  sortNodes(rootNodes);

  return { rootNodes, rootDocuments, folderMap };
}

/**
 * Obtener lista aplanada de carpetas para selectores,
 * con indentación indicada por depth.
 */
export function flattenFolders(rootNodes, depth = 0) {
  const result = [];
  for (const node of rootNodes) {
    result.push({ id: node.id, name: node.name, parent_id: node.parent_id, space_id: node.space_id, depth });
    result.push(...flattenFolders(node.children, depth + 1));
  }
  return result;
}

/**
 * Verificar si targetId es descendiente de sourceId
 * (para prevenir ciclos al mover carpetas)
 */
function isDescendant(folderMap, sourceId, targetId) {
  if (sourceId === targetId) return true;
  const target = folderMap[targetId];
  if (!target || !target.parent_id) return false;
  return isDescendant(folderMap, sourceId, target.parent_id);
}

// ── Hook principal ─────────────────────────────────────────────────────────

export function useFolders(caseId) {
  const [folders, setFolders]     = useState([]);
  const [spaces, setSpaces]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // ── Cargar espacios (los "boxes" de árboles de documentos) ───────────────

  const loadSpaces = useCallback(async () => {
    if (!caseId) return;
    try {
      const { data, error } = await supabase
        .from('document_space')
        .select('id, name, case_id, sort_order, created_at, created_by')
        .eq('case_id', caseId)
        .order('sort_order');

      if (error) throw error;
      setSpaces(data || []);
    } catch (err) {
      console.error('Error loading spaces:', err);
      setError(err.message);
    }
  }, [caseId]);

  useEffect(() => { loadSpaces(); }, [loadSpaces]);

  // ── Crear espacio ─────────────────────────────────────────────────────────

  const createSpace = useCallback(async (name, createdBy = null) => {
    const trimmed = name?.trim();
    if (!trimmed) throw new Error('El nombre no puede estar vacío');

    const { data, error } = await supabase
      .from('document_space')
      .insert({ case_id: caseId, name: trimmed, sort_order: spaces.length, created_by: createdBy })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') throw new Error('Ya existe una sección con ese nombre');
      throw error;
    }

    setSpaces(prev => [...prev, data]);
    return data;
  }, [caseId, spaces.length]);

  // ── Renombrar espacio ────────────────────────────────────────────────────

  const renameSpace = useCallback(async (spaceId, newName) => {
    const trimmed = newName?.trim();
    if (!trimmed) throw new Error('El nombre no puede estar vacío');

    const { data, error } = await supabase
      .from('document_space')
      .update({ name: trimmed })
      .eq('id', spaceId)
      .select()
      .single();

    if (error) {
      if (error.code === '23505') throw new Error('Ya existe una sección con ese nombre');
      throw error;
    }

    setSpaces(prev => prev.map(s => s.id === spaceId ? { ...s, name: trimmed } : s));
    return data;
  }, []);

  // ── Eliminar espacio (solo si está vacío; lo valida quien llama) ─────────

  const deleteSpace = useCallback(async (spaceId) => {
    const { error } = await supabase
      .from('document_space')
      .delete()
      .eq('id', spaceId);

    if (error) throw error;
    setSpaces(prev => prev.filter(s => s.id !== spaceId));
  }, []);

  // ── Cargar carpetas ──────────────────────────────────────────────────────

  const loadFolders = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('document_folder')
        .select('id, name, parent_id, case_id, created_at, created_by, space_id')
        .eq('case_id', caseId)
        .order('name');

      if (error) throw error;
      setFolders(data || []);
    } catch (err) {
      console.error('Error loading folders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  // ── Crear carpeta ────────────────────────────────────────────────────────

  const createFolder = useCallback(async (name, parentId = null, createdBy = null, spaceId = null) => {
    const trimmed = name?.trim();
    if (!trimmed) throw new Error('El nombre no puede estar vacío');

    const { data, error } = await supabase
      .from('document_folder')
      .insert({ case_id: caseId, name: trimmed, parent_id: parentId, created_by: createdBy, space_id: spaceId })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') throw new Error('Ya existe una carpeta con ese nombre aquí');
      throw error;
    }

    setFolders(prev => [...prev, data]);
    return data;
  }, [caseId]);

  // ── Renombrar carpeta ────────────────────────────────────────────────────

  const renameFolder = useCallback(async (folderId, newName) => {
    const trimmed = newName?.trim();
    if (!trimmed) throw new Error('El nombre no puede estar vacío');

    const { data, error } = await supabase
      .from('document_folder')
      .update({ name: trimmed })
      .eq('id', folderId)
      .select()
      .single();

    if (error) {
      if (error.code === '23505') throw new Error('Ya existe una carpeta con ese nombre aquí');
      throw error;
    }

    setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name: trimmed } : f));
    return data;
  }, []);

  // ── Eliminar carpeta ─────────────────────────────────────────────────────

  const deleteFolder = useCallback(async (folderId) => {
    // ON DELETE CASCADE se encarga de subcarpetas
    // ON DELETE SET NULL se encarga de documentos hijos (quedan en raíz)
    const { error } = await supabase
      .from('document_folder')
      .delete()
      .eq('id', folderId);

    if (error) throw error;
    setFolders(prev => prev.filter(f => f.id !== folderId && f.parent_id !== folderId));
  }, []);

  // ── Mover carpeta ────────────────────────────────────────────────────────

  const moveFolder = useCallback(async (folderId, newParentId, folderMap, newSpaceId) => {
    // Validar que no se crea un ciclo
    if (newParentId && isDescendant(folderMap, folderId, newParentId)) {
      throw new Error('No se puede mover una carpeta dentro de sí misma');
    }

    const updates = { parent_id: newParentId };
    if (newSpaceId) updates.space_id = newSpaceId;

    const { error } = await supabase
      .from('document_folder')
      .update(updates)
      .eq('id', folderId);

    if (error) throw error;

    // Si cambia de sección, toda la subrama (subcarpetas y sus documentos)
    // tiene que quedar en la misma sección para no quedar huérfana/invisible.
    let descendantIds = [];
    if (newSpaceId) {
      const collectDescendants = (id) => {
        const direct = Object.values(folderMap).filter(f => f.parent_id === id);
        return direct.flatMap(f => [f.id, ...collectDescendants(f.id)]);
      };
      descendantIds = collectDescendants(folderId);

      if (descendantIds.length > 0) {
        const { error: subError } = await supabase
          .from('document_folder')
          .update({ space_id: newSpaceId })
          .in('id', descendantIds);
        if (subError) throw subError;
      }

      const { error: docError } = await supabase
        .from('document')
        .update({ space_id: newSpaceId })
        .in('folder_id', [folderId, ...descendantIds]);
      if (docError) throw docError;
    }

    const descendantSet = new Set(descendantIds);
    setFolders(prev => prev.map(f => {
      if (f.id === folderId) return { ...f, parent_id: newParentId, ...(newSpaceId ? { space_id: newSpaceId } : {}) };
      if (newSpaceId && descendantSet.has(f.id)) return { ...f, space_id: newSpaceId };
      return f;
    }));
  }, []);

  // ── Mover documento ──────────────────────────────────────────────────────

  const moveDocument = useCallback(async (documentId, newFolderId, spaceId) => {
    const updates = { folder_id: newFolderId };
    if (spaceId) updates.space_id = spaceId;

    const { error } = await supabase
      .from('document')
      .update(updates)
      .eq('id', documentId);

    if (error) throw error;
  }, []);

  // ── Reordenar documentos (orden manual dentro de una carpeta/raíz) ───────

  const reorderDocuments = useCallback(async (orderedIds) => {
    await Promise.all(
      orderedIds.map((id, index) =>
        supabase.from('document').update({ sort_order: index }).eq('id', id)
      )
    );
  }, []);

  // ── Utilidades memoizadas ────────────────────────────────────────────────

  const folderMap = useMemo(() => {
    const map = {};
    for (const f of folders) map[f.id] = f;
    return map;
  }, [folders]);

  const flatFolders = useMemo(() => {
    // Para selectores: lista aplanada con depth
    const map = {};
    for (const f of folders) map[f.id] = { ...f, children: [] };
    const roots = [];
    for (const f of folders) {
      if (f.parent_id && map[f.parent_id]) map[f.parent_id].children.push(map[f.id]);
      else roots.push(map[f.id]);
    }
    roots.sort((a, b) => a.name.localeCompare(b.name));
    return flattenFolders(roots, 0);
  }, [folders]);

  return {
    folders,
    folderMap,
    flatFolders,
    spaces,
    loading,
    error,
    loadFolders,
    loadSpaces,
    createSpace,
    renameSpace,
    deleteSpace,
    createFolder,
    renameFolder,
    deleteFolder,
    moveFolder,
    moveDocument,
    reorderDocuments,
  };
}
